import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Layers, Briefcase, ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1723902701334-08b0fe53ff4c?crop=entropy&cs=srgb&fm=jpg&q=85";

const BADGES = [
  { icon: ShieldCheck, title: "One partner", desc: "For multiple solutions" },
  { icon: Layers, title: "End-to-end support", desc: "From brief to delivery" },
  { icon: Briefcase, title: "B2B focused", desc: "Built around your goals" },
];

const lineVariants = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.95, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden hero-mesh">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 min-h-screen flex flex-col justify-center pt-28 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="overflow-hidden">
              <motion.p
                initial="hidden"
                animate="show"
                custom={0}
                variants={lineVariants}
                className="font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-gold-dark"
              >
                Corporate Events · Media · Experiential Solutions
              </motion.p>
            </div>

            <h1 className="mt-6 font-display font-bold tracking-tight leading-[1.03] text-5xl sm:text-6xl xl:text-[76px] text-royal-deep">
              {[
                <>We bring your</>,
                <><span className="text-gold">big ideas</span></>,
                <>to life.</>,
              ].map((content, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    initial="hidden"
                    animate="show"
                    custom={i + 1}
                    variants={lineVariants}
                    className="block"
                  >
                    {content}
                  </motion.span>
                </span>
              ))}
            </h1>

            <div className="overflow-hidden mt-4">
              <motion.p
                initial="hidden"
                animate="show"
                custom={4}
                variants={lineVariants}
                className="max-w-xl text-sm sm:text-lg text-black/70 leading-relaxed"
              >
                From high-impact corporate events and exhibitions to thoughtful gifting, media
                and brand experiences — One Energy Club brings the people, partners and
                production together to make business moments matter.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href="#enquiry"
                data-testid="hero-cta-enquire"
                className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark hover:shadow-[0_8px_36px_rgba(224,19,39,0.45)]"
              >
                Tell us what you need
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-2.5 rounded-full border border-black/25 px-7 py-3.5 text-sm font-semibold text-royal-deep transition-all duration-300 hover:border-gold hover:text-gold-dark"
              >
                Explore our services
                <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 36, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="absolute -inset-3 rounded-[28px] border border-gold/40" />
            <div className="absolute -bottom-4 -right-4 h-full w-full rounded-[24px] bg-royal-soft -z-10" />
            <div className="relative overflow-hidden rounded-[24px] border border-black/10 shadow-[0_24px_70px_rgba(0,0,0,0.25)]">
              <motion.img
                style={{ y: imgY, scale: 1.12 }}
                src={HERO_IMG}
                alt="Corporate event on a large stage"
                data-testid="hero-image"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-royal-deep/55 via-transparent to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6 flex items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold-bright">
                    360° corporate solutions
                  </p>
                  <p className="mt-1 font-display text-base sm:text-lg font-semibold text-white">
                    Ideas → Impact. One connected experience.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 lg:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl"
        >
          {BADGES.map((b) => (
            <div
              key={b.title}
              data-testid="hero-badge"
              className="flex items-start gap-3.5 rounded-2xl border border-black/10 bg-white/80 backdrop-blur px-5 py-4 shadow-[0_4px_20px_rgba(0,0,0,0.06)]"
            >
              <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gold-soft text-gold-dark border border-gold/40">
                <b.icon size={16} />
              </span>
              <div>
                <p className="text-sm font-semibold text-royal-deep">{b.title}</p>
                <p className="text-xs text-black/60 mt-0.5">{b.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <div className="h-10 w-px bg-gradient-to-b from-transparent via-gold to-transparent" />
      </motion.div>
    </section>
  );
}
